import {
  BucketLocationType,
  BucketMovementStatus,
  BucketMovementType,
  type BucketContract,
  type BucketContractRule,
  type BucketMovement,
} from '@/integrations/agreements/client';
import { addDays, dayInOperationTimezone, describeAdvance, minAllowedDate } from '@/utils/onDemandRequestRules';

/** Textos do pedido de caçamba. Quem decide é o musa-agreements — aqui só se exibe. */

export const BUCKET_TYPE_LABELS: Record<BucketMovementType, string> = {
  [BucketMovementType.PLACEMENT]: 'Colocação',
  [BucketMovementType.REMOVAL]: 'Retirada',
  [BucketMovementType.SWAP]: 'Troca',
};

export const BUCKET_TYPE_DESCRIPTIONS: Record<BucketMovementType, string> = {
  [BucketMovementType.PLACEMENT]: 'Levar uma caçamba vazia até o endereço.',
  [BucketMovementType.REMOVAL]: 'Recolher uma caçamba que está no endereço.',
  [BucketMovementType.SWAP]: 'Recolher a cheia e deixar uma vazia no lugar, na mesma visita.',
};

export const BUCKET_LOCATION_LABELS: Record<BucketLocationType, string> = {
  [BucketLocationType.PUBLIC_ROAD]: 'Via pública',
  [BucketLocationType.PRIVATE_AREA]: 'Área privada',
};

export const BUCKET_STATUS_DISPLAY: Record<
  BucketMovementStatus,
  { label: string; variant: 'info' | 'primary' | 'success' | 'warning' | 'secondary' }
> = {
  [BucketMovementStatus.PENDING_HAULER]: { label: 'Pendente transportador', variant: 'info' },
  [BucketMovementStatus.PLANNED]: { label: 'Planejada', variant: 'primary' },
  [BucketMovementStatus.DONE]: { label: 'Realizada', variant: 'success' },
  [BucketMovementStatus.BLOCKED]: { label: 'Impedida', variant: 'warning' },
  [BucketMovementStatus.CANCELLED]: { label: 'Cancelada', variant: 'secondary' },
};

/** "2 na rua · 1 planejada · limite 3", ou sem o limite quando o contrato não tem. */
export function describeBucketUsage(
  contract: Pick<BucketContract, 'openCount' | 'plannedCount' | 'simultaneousLimit'>,
): string {
  const planned = `${contract.plannedCount} ${contract.plannedCount === 1 ? 'planejada' : 'planejadas'}`;
  const base = `${contract.openCount} na rua · ${planned}`;

  return contract.simultaneousLimit == null ? base : `${base} · limite ${contract.simultaneousLimit}`;
}

/** Prazo que a caçamba pode ficar no endereço, por local. */
export function describeBucketStayLimits(
  contract: Pick<BucketContract, 'maxDaysPublic' | 'maxDaysPrivate'>,
): string {
  const days = (n: number) => `${n} ${n === 1 ? 'dia' : 'dias'}`;
  return `Via pública: ${days(contract.maxDaysPublic)} · Área privada: ${days(contract.maxDaysPrivate)}`;
}

type SameDayRule = Pick<BucketContractRule, 'sameDayAllowed' | 'sameDayCutoff'>;

/** `HH:MM` de Brasília. */
function clockInOperationTimezone(now: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(now);
}

/** A regra aceita pedido para hoje e o horário de Brasília ainda não passou do máximo (inclusive). */
export function isSameDayOpen(rule: SameDayRule, now: Date = new Date()): boolean {
  if (!rule.sameDayAllowed || !rule.sameDayCutoff) return false;
  return clockInOperationTimezone(now) <= rule.sameDayCutoff;
}

/**
 * Primeiro dia do pedido de caçamba: hoje, se a regra aceita o mesmo dia e o horário não passou;
 * senão, a antecedência — o mesmo cálculo do serviço.
 */
export function bucketMinAllowedDate(rule: BucketContractRule, now: Date = new Date()): string {
  return isSameDayOpen(rule, now) ? dayInOperationTimezone(now) : minAllowedDate(rule, now);
}

/**
 * Janela de registro do musa-agreements (`BUCKET_WINDOW_DAYS`): colocação e retirada são
 * registradas até 90 dias depois. Pedido mais antigo que isso nunca seria registrado com a data em
 * que aconteceu.
 */
export const BUCKET_REGISTRATION_WINDOW_DAYS = 90;

/** Primeiro dia que a operação Musa pede com data passada — o mesmo piso do serviço. */
export function bucketBackdateFrom(now: Date = new Date()): string {
  return addDays(dayInOperationTimezone(now), -BUCKET_REGISTRATION_WINDOW_DAYS);
}

/**
 * "Pedido no mesmo dia até 14:00" ou "Antecedência mínima de 1 dia". Os dois são excludentes na
 * regra (MC-377): com o mesmo dia ligado, a antecedência nem aparece para quem configura.
 */
export function describeBucketAdvance(rule: BucketContractRule): string {
  return rule.sameDayAllowed && rule.sameDayCutoff ? `Pedido no mesmo dia até ${rule.sameDayCutoff}` : describeAdvance(rule);
}

/**
 * O número do que aconteceu vale mais; antes do registro, o que o gerador informou ao pedir a
 * retirada. Sem nenhum dos dois, quem informa é o transportador.
 */
export function describeBucketNumbers(movement: Pick<BucketMovement, 'bucketNumbers' | 'requestedBucketNumber'>): string {
  if (movement.bucketNumbers.length) return `Nº ${movement.bucketNumbers.join(', ')}`;
  if (movement.requestedBucketNumber) return `Nº ${movement.requestedBucketNumber} (informado no pedido)`;
  return 'Número definido pelo transportador';
}
